import React, { useEffect, useState } from 'react';
import { Breadcrumbs, Button, Grid } from '@decisiv/ui-components';
import { t } from '@lingui/macro';
import { useNavigate, useParams } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import PageTitle from '../../components/PageTitle/PageTitle';
import AsteriskSubtitle from '../../components/AsteriskSubtitle';
import BasicInformation from '../IntegrationCreatePage/basic_information';
import PaddedForm from '../../components/PaddedForm';
import IntegrationsApi from '../../api/integrations';
import WrapperWithLoading from '../../components/WrapperWithLoading';
import Scope from '../IntegrationCreatePage/scope';
import ServiceNetworkApi from '../../api/service_network';
import Trigger from '../IntegrationCreatePage/trigger';
import ApplicationEventsApi from '../../api/application_events';
import EditModal from './edit_modal';
import Commands from '../IntegrationCreatePage/commands';
import CommandsApi from '../../api/commands';
import CategoriesApi from '../../api/categories';
import ActionsApi from '../../api/actions';
import NotFound from '../../components/NotFound';

export default function IntegrationEditPage() {
  const navigate = useNavigate();
  const [integration, setIntegration] = useState({});
  const [integrationNotFound, setIntegrationNotFound] = useState(false);
  const [form, setForm] = useState({});
  const [serviceNetworkItems, setServiceNetworkItems] = useState([]);
  const [applicationEventsItems, setApplicationEventsItems] = useState([]);
  const [categories, setCategories] = useState([]);
  // PD-2651: switched to DB-backed `/categories` + `/actions`. `allActions`
  // is the full unlocked-latest list used by the picker; legacy
  // integrations whose `action_uuid` is null still load fine — we
  // pre-fill the picker by matching on the legacy action name and let the
  // user "migrate" the integration on save by re-picking from the new list.
  const [allActions, setAllActions] = useState([]);
  const [triggerTypeHttpRequest, setTriggerTypeHttpRequest] = useState(false);
  const [triggerTypeApplicationEvent, setTriggerTypeApplicationEvent] =
    useState(false);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [commands, setCommands] = useState([]);
  const [commandsFlow, setCommandsFlow] = useState(['']);
  const [categoryActions, setCategoryActions] = useState([]);
  const [saveBtnStatus, setSaveBtnStatus] = useState(false);

  const TRIGGER_TYPE_APPLICATION_EVENT = 'application_event';
  const TRIGGER_TYPE_HTTP_API_REQUEST = 'http_api_request';

  const params = useParams();

  function parseCommands({ data }) {
    const parsedData = data.map((command) => ({
      id: command.attributes.name,
      connections: command.attributes.connections,
    }));

    return parsedData;
  }

  // PD-2651: dropdown rows now carry the UUID as `id`/`value` so handleForm
  // can resolve back to a full record. Visible label stays the snake_case
  // name to match existing UX; swap to alias_name later if product wants it.
  function parseCategories({ data }) {
    return data.map((category) => ({
      id: category.id,
      label: category.attributes.name,
      value: category.id,
      name: category.attributes.name,
    }));
  }

  function parseActions(actions) {
    return actions.map((action) => ({
      id: action.id,
      label: action.attributes.name,
      value: action.id,
      name: action.attributes.name,
    }));
  }

  const handleForm = ({ event, selectedId, options, field, customValues }) => {
    const index = isEmpty(field) ? event.target.name : field;
    // Spread so setForm sees a new reference and React re-renders.
    const newForm = { ...form };

    switch (index) {
      // managing array value fields
      case 'audience':
      case 'trigger_type':
        if (event.target.checked) {
          newForm[index].push(event.target.value);
        } else {
          const itemIndex = newForm[index].indexOf(event.target.value);
          if (itemIndex !== -1) {
            newForm[index].splice(itemIndex, 1);
          }
        }
        break;
      case 'service_network':
      case 'events':
        // managing combobox multiple values fields
        /* istanbul ignore next */
        if (
          !newForm[index].includes(selectedId) &&
          !isEmpty(selectedId) &&
          !options.removed
        ) {
          newForm[index].push(selectedId);
        }

        /* istanbul ignore next */
        if (newForm[index].includes(selectedId) && options.removed) {
          const indexToRemove = newForm[index].indexOf(selectedId);
          if (indexToRemove !== -1) {
            newForm[index].splice(indexToRemove, 1);
          }
        }

        /* istanbul ignore next */
        integration.attributes[index] = newForm[index];
        /* istanbul ignore next */
        setIntegration(integration);

        /* istanbul ignore next */
        break;
      /* istanbul ignore next */
      case 'custom_events':
        newForm.events = customValues;
        integration.attributes.events = newForm.events;
        setIntegration(integration);

        break;
      /* istanbul ignore next */
      case 'commands_flow':
        newForm[index] = event;
        setCommandsFlow(event);
        break;
      /* istanbul ignore next */
      case 'action': {
        // selectedId is the Action UUID. Resolve to the full record so both
        // the legacy string and the new FK go on the form — backend's
        // sync_action_columns mirrors either, but explicit is clearest.
        // Picking an action here is also the implicit "migrate this
        // integration to the new structure" gesture for previously-
        // unmigrated rows (action_uuid was null before save).
        const selectedAction = allActions.find((a) => a.id === selectedId);
        newForm.action_uuid = selectedId || null;
        newForm.action = selectedAction?.attributes?.name || null;
        break;
      }
      /* istanbul ignore next */
      case 'category': {
        // selectedId is the Category UUID. Persist the category NAME
        // (legacy column is a string) and use the UUID only for filtering
        // actions.
        if (!isEmpty(selectedId)) {
          const selectedCategory = categories.find((c) => c.id === selectedId);
          newForm.category = selectedCategory?.name || null;
          const matching = allActions.filter(
            (a) => a.attributes?.category_uuid === selectedId,
          );
          setCategoryActions(parseActions(matching));
        } else {
          newForm.category = null;
          setCategoryActions([]);
        }
        // Changing category invalidates the prior action selection.
        newForm.action = null;
        newForm.action_uuid = null;
        break;
      }
      default:
        // managing text value fields
        /* istanbul ignore next */
        newForm[index] = event.target.value.includes('{')
          ? event.target.value.replace(/\n/g, '')
          : event.target.value;
    }

    setForm(newForm);

    /* istanbul ignore next */
    const btnStatus =
      isEmpty(newForm.category) ||
      isEmpty(newForm.name) ||
      isEmpty(newForm.audience) ||
      isEmpty(newForm.trigger_type) ||
      isEmpty(newForm.description.replace(/\s/g, '')) ||
      (newForm.trigger_type.includes(TRIGGER_TYPE_APPLICATION_EVENT) &&
        isEmpty(newForm.events)) ||
      (newForm.trigger_type.includes(TRIGGER_TYPE_HTTP_API_REQUEST) &&
        isEmpty(newForm.action));
    setSaveBtnStatus(btnStatus);

    setTriggerTypeApplicationEvent(
      newForm.trigger_type.includes(TRIGGER_TYPE_APPLICATION_EVENT),
    );
    setTriggerTypeHttpRequest(
      newForm.trigger_type.includes(TRIGGER_TYPE_HTTP_API_REQUEST),
    );
  };

  useEffect(() => {
    async function getData() {
      // PD-2651: parallel fetch of the two new endpoints replacing the
      // legacy `/categories-and-actions`. The picker shows latest unlocked
      // actions only — same UX rule applied in Create.
      const [responseCategories, responseActions] = await Promise.all([
        CategoriesApi.listCategories(),
        ActionsApi.fetchActionsList({
          locked: false,
          latest: true,
          limit: 200,
        }),
      ]);

      /* istanbul ignore else */
      if (responseCategories.data) {
        setCategories(parseCategories(responseCategories));
      }
      /* istanbul ignore else */
      if (responseActions.data) {
        setAllActions(responseActions.data);
      }

      const responseCommands = await CommandsApi.fetchCommands();
      /* istanbul ignore else */
      if (responseCommands.data) {
        const parsedCommands = parseCommands(responseCommands);
        setCommands(parsedCommands);
      }

      const integrationResponse = await IntegrationsApi.fetchIntegrationByUuid(
        params.id,
      );

      // The API client swallows the 404 and returns `{ errors }`, so an
      // integration deleted outside the UI leaves us with no data at all.
      if (isEmpty(integrationResponse.data)) {
        setIntegrationNotFound(true);
        setLoading(false);
        return;
      }

      // The router reuses this component when params.id changes, so the flag
      // has to be cleared for a valid id following an invalid one.
      setIntegrationNotFound(false);

      const attrs = integrationResponse.data.attributes;

      // Migration heuristic: if the integration already has action_uuid,
      // it's bound to a DB-backed Action. If it only has the legacy
      // `action` string, try to find a matching Action by name in
      // allActions — picking it on save will implicitly migrate the row.
      // If no match is found (action was disk-only, never backfilled),
      // the form keeps the legacy string and the runtime stays on the
      // disk-fallback path until the user picks a new Action.
      let resolvedActionUuid = attrs.action_uuid || null;
      if (!resolvedActionUuid && attrs.action && responseActions.data) {
        const match = responseActions.data.find(
          (a) => a.attributes?.name === attrs.action,
        );
        resolvedActionUuid = match?.id || null;
      }

      setForm({
        name: attrs.name,
        description: attrs.description,
        category: attrs.category,
        audience: attrs.audience,
        service_network: attrs.service_network,
        trigger_type: attrs.trigger_type,
        events: attrs.events,
        action: attrs.action,
        action_uuid: resolvedActionUuid,
      });
      setIntegration(integrationResponse.data);

      // Pre-populate the second dropdown using the resolved Action's
      // category UUID. Falls back to filtering by the legacy category
      // name when the integration is bound to an action that's not in
      // the DB-backed catalog yet.
      let matchingActions = [];
      if (resolvedActionUuid && responseActions.data) {
        const resolvedAction = responseActions.data.find(
          (a) => a.id === resolvedActionUuid,
        );
        const categoryUuid = resolvedAction?.attributes?.category_uuid;
        if (categoryUuid) {
          matchingActions = responseActions.data.filter(
            (a) => a.attributes?.category_uuid === categoryUuid,
          );
        }
      } else if (attrs.category && responseCategories.data) {
        const categoryByName = responseCategories.data.find(
          (c) => c.attributes?.name === attrs.category,
        );
        if (categoryByName) {
          matchingActions = (responseActions.data || []).filter(
            (a) => a.attributes?.category_uuid === categoryByName.id,
          );
        }
      }
      setCategoryActions(parseActions(matchingActions));

      setTriggerTypeApplicationEvent(
        attrs.trigger_type.includes(TRIGGER_TYPE_APPLICATION_EVENT),
      );
      setTriggerTypeHttpRequest(
        attrs.trigger_type.includes(TRIGGER_TYPE_HTTP_API_REQUEST),
      );

      setCommandsFlow(attrs.commands_flow);
      setLoading(false);

      const responseServiceNetwork =
        await ServiceNetworkApi.fetchServiceNetwork();
      /* istanbul ignore else */
      if (responseServiceNetwork.data) {
        const parsedServiceNetworks = responseServiceNetwork.data.map((sn) => ({
          id: sn.attributes.name,
          label: sn.attributes.name,
          value: sn.attributes.name,
        }));
        setServiceNetworkItems(parsedServiceNetworks);
      }

      const responseApplicationEvents =
        await ApplicationEventsApi.fetchApplicationEvents();
      /* istanbul ignore else */
      if (responseApplicationEvents.data) {
        const parsedApplicationEvents = responseApplicationEvents.data.map(
          (sn) => ({
            id: sn.attributes.name,
            label: sn.attributes.name,
            value: sn.attributes.name,
          }),
        );
        setApplicationEventsItems(parsedApplicationEvents);
      }
    }

    getData();
  }, [params.id, integration.length, form.length]);

  // Refresh just the picker's reference data (categories + actions) on focus /
  // bfcache restore — not the main `getData`, which would clobber form edits.
  useEffect(() => {
    const refreshPickerData = async () => {
      const [responseCategories, responseActions] = await Promise.all([
        CategoriesApi.listCategories(),
        ActionsApi.fetchActionsList({
          locked: false,
          latest: true,
          limit: 200,
        }),
      ]);
      if (responseCategories.data) {
        setCategories(parseCategories(responseCategories));
      }
      if (responseActions.data) {
        setAllActions(responseActions.data);
      }
    };

    const onFocus = () => refreshPickerData();
    const onPageShow = (e) => {
      if (e.persisted) refreshPickerData();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  const handleSave = () => {
    setShowEditModal(true);
  };

  const handleToggleEditModal = () => setShowEditModal(!showEditModal);

  if (integrationNotFound) {
    return (
      <>
        <Breadcrumbs
          path={[{ to: '/integrations', text: 'Integrations' }]}
          current={t`Edit Integration`}
          style={{ padding: '0.375rem 1.3125rem' }}
        />
        <NotFound
          title={t`Integration Not Found`}
          description={t`The integration may have been archived or the URL is incorrect.`}
        />
      </>
    );
  }

  return (
    <>
      <EditModal
        handleToggleEditModal={handleToggleEditModal}
        showEditModal={showEditModal}
        form={form}
        integration={integration}
      />

      <Breadcrumbs
        path={[{ to: '/integrations', text: 'Integrations' }]}
        current={t`Edit Integration`}
        style={{ padding: '0.375rem 1.3125rem' }}
      />
      <PageTitle title={t`Edit Integration`} subtitle={<AsteriskSubtitle />}>
        <Button
          aria-label={t`Save Edit Integration`}
          id="btn-save-integration"
          text={t`Save`}
          disabled={saveBtnStatus}
          onClick={handleSave}
        />
        <Button
          aria-label={t`Cancel`}
          text={t`Cancel`}
          kind="secondary"
          onClick={() =>
            navigate(`/integrations/${integration?.id}`)
          } /* istanbul ignore */
        />
      </PageTitle>

      <WrapperWithLoading loading={loading}>
        <PaddedForm>
          <Grid.Container>
            <BasicInformation
              handleForm={handleForm}
              integration={integration}
              categories={categories}
            />
            <Scope
              handleForm={handleForm}
              serviceNetworkItems={serviceNetworkItems}
              integration={integration}
              editMode
            />
            <Trigger
              handleForm={handleForm}
              applicationEventsItems={applicationEventsItems}
              triggerTypeHttpRequest={triggerTypeHttpRequest}
              triggerTypeApplicationEvent={triggerTypeApplicationEvent}
              integration={integration}
              editMode
            />
            <Commands
              handleForm={handleForm}
              commands={commands}
              commandsFlow={commandsFlow}
              categoryActions={categoryActions}
              triggerTypeHttpRequest={triggerTypeHttpRequest}
              editMode
              integration={integration}
            />
          </Grid.Container>
        </PaddedForm>
      </WrapperWithLoading>
    </>
  );
}
